/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { useState, useEffect } from 'react';
import { Toolbar } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { dashboardActions } from 'modules/dashboard/slice';
import { trendActions } from 'modules/dashboard/components/tab-container/trend-tabs/slice';
import {
  selectVisualFilters,
  selectPmsSync,
  selectTrendTargetDate,
  selectTrendOtb,
  selectTrendBookingStyle,
  selectIsSignedIn,
  selectBreakdownDetails,
  selectHotelName,
  selectHotelDropdownList,
  selectEnableHotelGroups,
  selectHotelGroupDropdownList,
  selectMergedHotelList,
  selectsHotelForecast,
  selectPredefinedHotelGroups,
  selectIsDemoModeActivated,
  selectResetBookingDate,
  selectTabList,
} from 'modules/dashboard/selectors';
import {
  selectIsCumulative,
  selectTrendTimeCustomDate,
  selectTrendTimeEndDate,
  selectTrendTimeStartDate,
  selectTrendTimeWeeklyView,
  selectTrendSymmetricComparisonToggle,
  selectTrendTimeAlignmentToggle,
  selectTrendTimeDateComparison,
  selectTrendTimeAlignment,
  selectTrendHotelFocusOn,
  selectIsSegmentDetailed,
  selectSegmentSetAName,
  selectSegmentSetBName,
  selectSelectedFilterName,
  selectShowCustomGroupingFilterName,
  selectTrendSegmentChartBreakdown,
  selectTrendSegmentFocusOn,
  selectTrendHotelNormalizedView,
  selectEnableSecondaryHotelGroups,
  selectIsForecast,
  selectIsForecastDisabled,
  selectActiveTrendTab,
  selectSymmetricComparisonIsDisplay,
  selectIsPrimary,
  selectIsPercentageChange,
  selectIsPercentageChangeDisabled,
  selectIsTrendDeAggregate,
  selectIsTrendDeAggregateDisabled,
  selectTabularView,
  selectIsTrendTimeWeeklyViewDisabled,
  selectIsMovingAverage,
  selectMovingAverage,
  selectIsFocusOnSet,
  selectIsFocusAllSelected,
  selectRevenueIsDisplayTT,
  selectOriginalHotelFocusOn,
} from 'modules/dashboard/components/tab-container/trend-tabs/selectors';
import {
  getComparisonDateRange,
  getSelectedBreakdown,
  shortcut,
  timeout,
} from 'modules/dashboard/functions';
import { capitalizeFirst } from 'modules/common/helpers/strings';
import {
  SHORTCUT_KEY,
  OTB_VALUE,
  KEYS,
  TREND_STAY_DATE,
  TAGS,
  REPORT_TYPE,
  ALIGNED,
} from 'modules/dashboard/constants';
import ActionButton from 'modules/common/components/action-button';
import { CustomStack } from 'modules/common/components';
import styles from 'modules/dashboard/components/tab-container/tab-container.module.scss';
import { isSaturday, isSunday, nextSaturday, previousSunday } from 'date-fns';
import DataUpdatedLabel from 'modules/dashboard/components/tab-container/data-updated-label';
import PmsToggle from 'modules/dashboard/components/tab-container/inner-filter/components/pms-toggle';
import WeeklyCumulativeWindow from 'modules/dashboard/components/tab-container/inner-filter/components/weekly-cumulative-window';
import BookingWindow from 'modules/dashboard/components/tab-container/inner-filter/components/booking-window';
import TrendTimeVsComparisonWindow from 'modules/dashboard/components/tab-container/inner-filter/components/trend-time-vs-compare-window';
import {
  getDateComparisonFilter,
  getFocusOnFilter,
  getMovingAverageFilter,
  getSubAlignmentFilter,
  getSubBreakdownFilter,
} from 'modules/dashboard/components/tab-container/inner-filter/functions';
import PAGINATION from 'modules/common/constants/pagination-limits';

import { CompareHotelsWindow } from 'modules/dashboard/components/tab-container/inner-filter';
import {
  selectAuthUser,
  selectOrganization,
  selectOrganizationId,
} from 'modules/common/auth/selectors';
import useTrendTabularCommandPipeline from '../../hooks/use-trend-tabular-command-pipeline';
import MovingAverageWindow from '../../../inner-filter/components/moving-average-window';
import TrendBreakdownWindow from '../../../inner-filter/components/breakdown-window/trend-breakdown';
import { ENVIRONMENT } from '../../../../../../../config';
import { isEmpty } from '../../../../../../common/helpers/object';
import { COMMON_FOCUS_ON_OPTIONS } from '../../../trend-tabs/constants';
import GranularityRadio from '../../../inner-filter/components/granularity-radio';

/**
 * Filter panel implementation for Trend Widget
 * @param {Number} tabValue - Pace widget tab value
 * @param {Date} latestDate - Latest booking date of selected hotel
 * @param {Boolean} hotelTimezone - Timezone of selected hotel
 * @param {Number} toggleDisabled - PMS sync toggle disabled state
 * @param {Boolean} isLoaded - Visual is loaded state
 * @param {String} reportType - Report type of active tab
 * @param {Object} visual- Symmetric comparison visual object
 * @param {String} visualName - Display name for symmetric comparison visual
 * @returns
 */
const TrendFilter = ({
  reportType,
  visual,
  visualName,
  tabValue,
  toggleDisabled,
  latestDate,
  hotelTimezone,
  isLoaded,
}) => {
  const dispatch = useDispatch();
  // Selected filter values are obtained from redux store. Refer each selector function for details.
  const organization = useSelector(selectOrganization);
  const selectedHotel = useSelector(selectHotelName);
  const customBooking = useSelector(selectTrendBookingStyle);
  const vOTB = useSelector(selectTrendOtb);
  const alignment = useSelector(selectTrendTimeAlignment);
  const dateComparison = useSelector(selectTrendTimeDateComparison);
  const visualFilters = useSelector(selectVisualFilters);
  const pmsSyncToggle = useSelector(selectPmsSync);
  const weeklyViewToggle = useSelector(selectTrendTimeWeeklyView);
  const alignmentToggle = useSelector(selectTrendTimeAlignmentToggle);
  const targetDate = useSelector(selectTrendTargetDate);
  const symmetricComparisonToggle = useSelector(selectTrendSymmetricComparisonToggle);
  const isSignIn = useSelector(selectIsSignedIn);
  const isCumulative = useSelector(selectIsCumulative);
  const customDate = useSelector(selectTrendTimeCustomDate);
  const startDate = useSelector(selectTrendTimeStartDate);
  const endDate = useSelector(selectTrendTimeEndDate);
  const isTrendForecast = useSelector(selectIsForecast);
  const isDetailed = useSelector(selectIsSegmentDetailed);
  const focusOn = useSelector(selectTrendSegmentFocusOn);
  const chartBreakdown = useSelector(selectTrendSegmentChartBreakdown);
  const breakdownData = useSelector(selectBreakdownDetails);
  const selectedFilterName = useSelector(selectSelectedFilterName);
  const showFilterName = useSelector(selectShowCustomGroupingFilterName);
  const setAName = useSelector(selectSegmentSetAName);
  const setBName = useSelector(selectSegmentSetBName);
  const hotelFocusOn = useSelector(selectTrendHotelFocusOn);
  const hotelList = useSelector(selectHotelDropdownList);
  const normalizedViewToggle = useSelector(selectTrendHotelNormalizedView);
  const enableSecondaryHotelGroups = useSelector(selectEnableSecondaryHotelGroups);
  const hotelGroupList = useSelector(selectHotelGroupDropdownList);
  const enableHotelGroups = useSelector(selectEnableHotelGroups);
  const isForecastDisabled = useSelector(selectIsForecastDisabled);
  const activeTab = useSelector(selectActiveTrendTab);
  const symmetricComparisonIsDisplay = useSelector(selectSymmetricComparisonIsDisplay);
  const isPrimary = useSelector(selectIsPrimary);
  const isTrendPercentageChange = useSelector(selectIsPercentageChange);
  const isTrendPercentageDisabled = useSelector(selectIsPercentageChangeDisabled);
  const isTrendDeAggregate = useSelector(selectIsTrendDeAggregate);
  const isTrendDeAggregateDisabled = useSelector(selectIsTrendDeAggregateDisabled);
  const isTabular = useSelector(selectTabularView);
  const isWeeklyViewDisabled = useSelector(selectIsTrendTimeWeeklyViewDisabled);
  const mergedHotelList = useSelector(selectMergedHotelList);
  const isMovingAverage = useSelector(selectIsMovingAverage);
  const movingAverage = useSelector(selectMovingAverage);
  const isHotelForecast = useSelector(selectsHotelForecast);
  const organizationId = useSelector(selectOrganizationId);
  const currentUser = useSelector(selectAuthUser);
  const predefinedHotelGroups = useSelector(selectPredefinedHotelGroups);
  const isFocusOnSet = useSelector(selectIsFocusOnSet);
  const allSelected = useSelector(selectIsFocusAllSelected);
  const revenueIsDisplay = useSelector(selectRevenueIsDisplayTT);
  const originalHotelFocusOn = useSelector(selectOriginalHotelFocusOn);
  const isDemoModeActivated = useSelector(selectIsDemoModeActivated);
  const resetBookingDate = useSelector(selectResetBookingDate);
  const TAB_VALUE = useSelector(selectTabList);
  //
  const [dateComparisonList, setDateComparisonList] = useState([]);
  const [chartBreakdownList, setChartBreakdownList] = useState([]);
  const [previousFocusOnSelection, setPreviousFocusOnSelection] = useState([]);
  const [focusOnList, setFocusOnList] = useState([]);
  // breakdown that the current focus on list was built for
  const [focusOnListBreakdown, setFocusOnListBreakdown] = useState();
  const [selectedBreakdown, setSelectedBreakdown] = useState();
  const [movingAverageList, setMovingAverageList] = useState([]);
  const [open, setOpen] = useState(false);
  //
  const serializedState = JSON.parse(localStorage.getItem(`persist:${ENVIRONMENT.PERSIST_KEY}`));
  //
  useEffect(() => {
    if (focusOn?.length > 0 && !isFocusOnSet) {
      dispatch(trendActions.setIsFocusOnSet(true));
    }
  }, [focusOn, isFocusOnSet]);
  // To select otb or custom booking window
  const handleOTBChange = (event) => {
    const { value } = event.target;
    dispatch(dashboardActions.setTrendOtb(value));
    dispatch(dashboardActions.setTrendBookingStyle(!customBooking));
    dispatch(dashboardActions.setTrendBookingDate(resetBookingDate));
  };
  // Reset button function to set filter panel values to default
  const clearSearch = async () => {
    dispatch(trendActions.setIsPrimary(false));
    dispatch(dashboardActions.setTrendOtb(0));
    dispatch(dashboardActions.setTrendBookingStyle(false));
    const subAlignmentFilter = getSubAlignmentFilter(visualFilters, false);
    dispatch(
      trendActions.setTrendTimeDateComparison(
        subAlignmentFilter?.values?.filter((filter) => filter.isDefault === true)[0].value
      )
    );

    dispatch(trendActions.setTrendTimeAlignmentToggle(subAlignmentFilter?.type === ALIGNED));
    dispatch(dashboardActions.setTrendBookingDate(resetBookingDate));
    dispatch(dashboardActions.setPmsSync(false));
    dispatch(trendActions.setTrendTimeWeeklyView(false));
    dispatch(trendActions.setTrendSymmetricComparisonToggle(true));
    await timeout(2000);
    dispatch(trendActions.setIsCumulative(false));
    dispatch(trendActions.setTrendTimeStartDate(TREND_STAY_DATE.startDate));
    dispatch(trendActions.setTrendTimeEndDate(TREND_STAY_DATE.endDate));
    dispatch(trendActions.setTrendTimeCustomDate(KEYS.START_DATE));
    dispatch(trendActions.setTrendTimeDateSelected(TREND_STAY_DATE.startDate));
    dispatch(
      trendActions.setTrendSegmentChartBreakdown(
        visualFilters?.filter(
          (filter) => filter?.tags?.includes(TAGS.SEGMENT_BREAKDOWN) && filter?.isDefault === true
        )[0].id
      )
    );
    const movingAverageFilter = visualFilters?.find(
      (filter) => filter?.tags?.includes(TAGS.MOVING_AVERAGE) && filter?.isDefault === true
    );
    dispatch(
      trendActions.setMovingAverage({
        id: movingAverageFilter?.id,
        label: movingAverageFilter?.values[0],
      })
    );
    dispatch(trendActions.setIsMovingAverage(false));
    dispatch(trendActions.setIsSegmentDetailed(false));
    dispatch(trendActions.setIsTrendForecast(false));
    dispatch(trendActions.setIsTrendPercentageChange(false));
    dispatch(trendActions.setTrendHotelNormalizedView(false));
    // Select all options if "Select All" is not selected
    const allOptions = focusOnList.filter(
      (option) =>
        option.label !== COMMON_FOCUS_ON_OPTIONS.SELECT_ALL.label &&
        option.label !== COMMON_FOCUS_ON_OPTIONS.OVERALL.label
    );
    dispatch(trendActions.setTrendSegmentFocusOn(allOptions));
    dispatch(trendActions.setIsFocusAllSelected(true));
    dispatch(trendActions.setSegmentGroup1([]));
    dispatch(trendActions.setSegmentGroup2([]));
    dispatch(
      trendActions.setIsForecastDisabled(
        (!organization?.enableForecastFilter || !isHotelForecast) ?? false
      )
    );
    dispatch(trendActions.setIsTrendDeAggregate(false));
    dispatch(trendActions.setSymmetricComparisonIsDisplay(false));
    dispatch(trendActions.setTabularView(false));
    setPreviousFocusOnSelection(
      chartBreakdownList.reduce((acc, breakdown) => {
        acc[breakdown.id] = { detailedOn: [], detailedOff: [] };
        return acc;
      }, {})
    );
  };
  // set custom date comparison value
  const setArbitraryComparisonDate = () => {
    dispatch(trendActions.setTrendTimeDateSelected(TREND_STAY_DATE.startDate));
    dispatch(trendActions.setTrendTimeCustomDate(KEYS.START_DATE));
    const dateRange = getComparisonDateRange(
      TREND_STAY_DATE.startDate,
      targetDate.startDate,
      targetDate.endDate,
      KEYS.START_DATE
    );
    //
    dispatch(trendActions.setTrendTimeStartDate(dateRange?.comparisonStartDate.toISOString()));
    dispatch(trendActions.setTrendTimeEndDate(dateRange?.comparisonEndDate.toISOString()));
  };
  // Date comparison checkbox change handler
  const handleDateComparisonChange = (event) => {
    const { value } = event.target;
    dispatch(trendActions.setTrendTimeDateComparison(value));
    if (value === KEYS.CUSTOM) {
      setArbitraryComparisonDate();
    }
  };
  // Triggered when values of alignment, visualFilters, alignmentToggle change
  useEffect(() => {
    // Set date comparison list based on alignment
    if (alignment) {
      const dateComparisonFilter = getDateComparisonFilter(visualFilters, alignment?.id);
      setDateComparisonList(dateComparisonFilter);
      // Persist values on browser refresh
      if (!isSignIn && serializedState) {
        return;
      }
      dispatch(
        trendActions.setTrendTimeDateComparison(
          dateComparisonFilter?.filter((filter) => filter?.isDefault === true)[0].value
        )
      );
    }
  }, [alignment, visualFilters, alignmentToggle]);
  // Triggered when alignmentToggle, visualFilters values change
  useEffect(() => {
    // Set alignment list
    const subAlignmentFilter = getSubAlignmentFilter(visualFilters, alignmentToggle);
    dispatch(
      trendActions.setTrendTimeAlignment({
        id: subAlignmentFilter?.id,
        label: capitalizeFirst(subAlignmentFilter?.type),
      })
    );
  }, [alignmentToggle, visualFilters]);

  // Set alignment toggle on if weekly toggle is on
  useEffect(() => {
    if (weeklyViewToggle) dispatch(trendActions.setTrendTimeAlignmentToggle(true));
  }, [weeklyViewToggle]);

  // Triggered when targetDate, weeklyViewToggle value changes
  useEffect(() => {
    // Set stay dates based on weekly toggle
    if (weeklyViewToggle) {
      if (isSunday(new Date(targetDate.startDate))) {
        dispatch(
          dashboardActions.setWeeklyTrendStartDate(new Date(targetDate.startDate).toISOString())
        );
      } else {
        dispatch(
          dashboardActions.setWeeklyTrendStartDate(
            previousSunday(new Date(targetDate.startDate)).toISOString()
          )
        );
      }
      if (isSaturday(new Date(targetDate.endDate))) {
        dispatch(
          dashboardActions.setWeeklyTrendEndDate(new Date(targetDate.endDate).toISOString())
        );
      } else {
        dispatch(
          dashboardActions.setWeeklyTrendEndDate(
            nextSaturday(new Date(targetDate.endDate)).toISOString()
          )
        );
      }
    }
  }, [targetDate, weeklyViewToggle]);
  // Triggered when visualFilters value changes
  useEffect(() => {
    // Set breakdown list
    const subBreakdownFilter = getSubBreakdownFilter(visualFilters, TAGS.TREND);
    if (subBreakdownFilter) {
      setChartBreakdownList(subBreakdownFilter);
      setPreviousFocusOnSelection(
        subBreakdownFilter.reduce((acc, breakdown) => {
          acc[breakdown.id] = { detailedOn: [], detailedOff: [] };
          return acc;
        }, {})
      );
    }
    const movingAvgList = getMovingAverageFilter(visualFilters);
    if (movingAvgList) {
      setMovingAverageList(movingAvgList);
    }
  }, [visualFilters]);
  // Triggered when the values of isDetailed, chartBreakdown or visualFilters change
  useEffect(() => {
    // Assign values to selected breakdown on chart breakdown or isDetails toggle change
    const breakdown = getSelectedBreakdown(visualFilters, chartBreakdown, isDetailed);
    setSelectedBreakdown(breakdown);
  }, [isDetailed, chartBreakdown, visualFilters]);
  //
  useEffect(() => {
    // set breakdown focus on filter list

    if (chartBreakdown) {
      const focusOnFilter = getFocusOnFilter(
        breakdownData,
        selectedBreakdown,
        isPrimary,
        isTrendDeAggregate
      );
      setFocusOnList(focusOnFilter);
      setFocusOnListBreakdown(selectedBreakdown);
      if (isEmpty(previousFocusOnSelection)) {
        dispatch(
          trendActions.setTrendSegmentFocusOn(
            focusOnFilter?.filter(
              (option) => option?.label !== COMMON_FOCUS_ON_OPTIONS.SELECT_ALL.label
            )
          )
        );
      }
      if (
        isDetailed &&
        !isEmpty(previousFocusOnSelection) &&
        isEmpty(previousFocusOnSelection[chartBreakdown].detailedOn)
      ) {
        dispatch(
          trendActions.setTrendSegmentFocusOn(
            focusOnFilter?.filter(
              (option) => option?.label !== COMMON_FOCUS_ON_OPTIONS.SELECT_ALL.label
            )
          )
        );
      } else if (
        !isDetailed &&
        !isEmpty(previousFocusOnSelection) &&
        isEmpty(previousFocusOnSelection[chartBreakdown].detailedOff)
      ) {
        dispatch(
          trendActions.setTrendSegmentFocusOn(
            focusOnFilter?.filter(
              (option) => option?.label !== COMMON_FOCUS_ON_OPTIONS.SELECT_ALL.label
            )
          )
        );
      }
    }
  }, [
    chartBreakdown,
    visualFilters,
    breakdownData,
    selectedBreakdown,
    isPrimary,
    isTrendDeAggregate,
  ]);
  //
  useEffect(() => {
    const focusOnFilter = getFocusOnFilter(
      breakdownData,
      selectedBreakdown,
      isPrimary,
      isTrendDeAggregate
    );
    if (!isPrimary) {
      dispatch(
        trendActions.setTrendSegmentFocusOn(
          focusOnFilter?.filter(
            (option) =>
              option?.label !== COMMON_FOCUS_ON_OPTIONS.OVERALL &&
              option?.label !== COMMON_FOCUS_ON_OPTIONS.SELECT_ALL.label
          )
        )
      );
    }
  }, [isPrimary]);
  //
  useEffect(() => {
    // Assign forecast disabled state on breakdown focus on change, percentage toggle and active tab change
    if (activeTab === REPORT_TYPE.TREND_SEGMENT || !allSelected) {
      dispatch(trendActions.setIsForecastDisabled(true));
      dispatch(trendActions.setIsTrendForecast(false));
    } else if (isTrendPercentageChange) {
      dispatch(trendActions.setIsForecastDisabled(true));
      dispatch(trendActions.setIsTrendForecast(false));
    } else if (isMovingAverage) {
      dispatch(trendActions.setIsForecastDisabled(true));
      dispatch(trendActions.setIsTrendForecast(false));
    } else {
      dispatch(
        trendActions.setIsForecastDisabled(
          (!organization?.enableForecastFilter || !isHotelForecast) ?? false
        )
      );
    }
  }, [
    isDetailed,
    chartBreakdown,
    focusOn,
    activeTab,
    organization,
    isTrendDeAggregate,
    isTrendPercentageChange,
    isMovingAverage,
    isHotelForecast,
    allSelected,
  ]);
  //
  useEffect(() => {
    // Assign percentage disabled state on cumulative view, weekly view normalized view
    if (
      isCumulative ||
      weeklyViewToggle ||
      normalizedViewToggle ||
      isTrendDeAggregate ||
      !isMovingAverage
    ) {
      dispatch(trendActions.setIsTrendPercentageChange(false));
      dispatch(trendActions.setIsTrendPercentageChangeDisabled(true));
    } else {
      dispatch(trendActions.setIsTrendPercentageChangeDisabled(false));
    }
  }, [
    isCumulative,
    weeklyViewToggle,
    normalizedViewToggle,
    isTrendPercentageChange,
    isTrendDeAggregate,
    isMovingAverage,
  ]);
  //
  useEffect(() => {
    // Assign percentage disabled state on cumulative view, weekly view normalized view
    if (activeTab !== REPORT_TYPE.TREND_SEGMENT) {
      dispatch(trendActions.setIsTrendDeAggregate(false));
      dispatch(trendActions.setIsTrendDeAggregateDisabled(true));
    } else if (activeTab === REPORT_TYPE.TREND_SEGMENT && isTrendPercentageChange) {
      dispatch(trendActions.setIsTrendDeAggregate(false));
      dispatch(trendActions.setIsTrendDeAggregateDisabled(true));
    } else {
      dispatch(trendActions.setIsTrendDeAggregateDisabled(false));
    }
  }, [activeTab, isTrendPercentageChange]);
  useEffect(() => {
    // Assign weekly view disabled state on cumulative view, active tab and tabular view
    if (isTabular && isCumulative) {
      dispatch(trendActions.setTrendTimeWeeklyView(false));
      dispatch(trendActions.setTrendTimeWeeklyViewDisabled(true));
    } else {
      dispatch(trendActions.setTrendTimeWeeklyViewDisabled(false));
    }
  }, [isTabular, isCumulative]);
  // Triggered when dateComparison value changes
  useEffect(() => {
    if (dateComparison) {
      if (!dateComparison.includes(KEYS.CUSTOM)) {
        setArbitraryComparisonDate();
      }
    }
  }, [dateComparison]);
  // // set all focus on options when trend segment is selected with overall option
  useEffect(() => {
    if (
      isDetailed &&
      !isEmpty(previousFocusOnSelection) &&
      previousFocusOnSelection[chartBreakdown] &&
      !isEmpty(previousFocusOnSelection[chartBreakdown].detailedOn)
    ) {
      dispatch(
        trendActions.setTrendSegmentFocusOn(previousFocusOnSelection[chartBreakdown].detailedOn)
      );
    } else if (
      !isDetailed &&
      !isEmpty(previousFocusOnSelection) &&
      !isEmpty(previousFocusOnSelection[chartBreakdown].detailedOff)
    ) {
      dispatch(
        trendActions.setTrendSegmentFocusOn(previousFocusOnSelection[chartBreakdown].detailedOff)
      );
    }
  }, [isPrimary, focusOnList, isTrendDeAggregate]);

  // check whether all focus on options are selected or not
  useEffect(() => {
    if (!isEmpty(focusOnList) && focusOnList?.length > 0) {
      if (activeTab === REPORT_TYPE.TREND_SEGMENT && !isTrendDeAggregate) {
        const allFocusOnOptionsSelected =
          focusOnList.length - 2 ===
          focusOn?.filter((option) => option.label !== COMMON_FOCUS_ON_OPTIONS.OVERALL.label)
            .length;
        dispatch(trendActions.setIsFocusAllSelected(allFocusOnOptionsSelected));
      } else {
        const allFocusOnOptionsSelected = focusOnList.length - 1 === focusOn?.length;
        dispatch(trendActions.setIsFocusAllSelected(allFocusOnOptionsSelected));
      }
    }
  }, [focusOn, focusOnList, activeTab, isTrendDeAggregate]);

  // Triggered when customBooking, dateComparison, visualFilters values change
  useEffect(() => {
    // Assign default value to date comparison when OTB is selected for booking window
    if (!customBooking && dateComparison === OTB_VALUE && visualFilters) {
      const subAlignmentFilter = getSubAlignmentFilter(visualFilters, true);
      dispatch(
        trendActions.setTrendTimeDateComparison(
          subAlignmentFilter?.values?.find((filter) => filter.isDefault === true)[0]?.value
        )
      );
    }
  }, [customBooking, dateComparison, visualFilters]);
  // Triggered when weeklyViewToggle and setIsCumulative
  useEffect(() => {
    if (isCumulative || weeklyViewToggle) {
      dispatch(trendActions.setIsMovingAverage(false));
    }
  }, [isCumulative, weeklyViewToggle]);
  // Shortcut function to weekly view toggle
  shortcut(tabValue, TAB_VALUE.TREND_TIME, SHORTCUT_KEY.WEEKLY_TOGGLE, () =>
    dispatch(trendActions.setTrendTimeWeeklyView(!weeklyViewToggle))
  );
  // Shortcut function to set pms sync value
  shortcut(tabValue, TAB_VALUE.TREND_TIME, SHORTCUT_KEY.PMS_TOGGLE, () =>
    dispatch(dashboardActions.setPmsSync(!pmsSyncToggle))
  );
  // Alignment toggle change handler
  const handleAlignmentToggleChange = (event) => {
    dispatch(trendActions.setTrendTimeAlignmentToggle(event.target.checked));
    const dateComparisonFilter = getDateComparisonFilter(visualFilters, alignment?.id);
    setDateComparisonList(dateComparisonFilter);
    dispatch(
      trendActions.setTrendTimeDateComparison(
        dateComparisonFilter?.filter((filter) => filter?.isDefault === true)[0].value
      )
    );
  };
  // Reset focus on custom aggregation values
  const resetOptions = () => {
    dispatch(trendActions.setTrendSegmentFocusOn([]));
    dispatch(trendActions.setSegmentGroup1([]));
    dispatch(trendActions.setSegmentGroup2([]));
    const subBreakdownFilter = getSubBreakdownFilter(visualFilters);
    setPreviousFocusOnSelection(
      !isEmpty(subBreakdownFilter) &&
        subBreakdownFilter?.reduce((acc, breakdown) => {
          acc[breakdown.id] = { detailedOn: [], detailedOff: [] };
          return acc;
        }, {})
    );
  };
  // reset focus on option when hotel group option is selected
  useEffect(() => {
    resetOptions();
  }, [selectedHotel, enableHotelGroups]);
  /**
   * set breakdown value
   * @param {*} event - onChanged event
   */
  const handleBreakdownChange = (event) => {
    if (previousFocusOnSelection[chartBreakdown]) {
      if (isDetailed) {
        previousFocusOnSelection[chartBreakdown].detailedOn = focusOn;
      } else {
        previousFocusOnSelection[chartBreakdown].detailedOff = focusOn;
      }
    }
    dispatch(trendActions.setTrendSegmentChartBreakdown(event.target.value));
    dispatch(
      trendActions.setTrendSegmentFocusOn(
        isDetailed
          ? previousFocusOnSelection[event.target.value]?.detailedOn
          : previousFocusOnSelection[event.target.value]?.detailedOff
      )
    );
    dispatch(trendActions.setSegmentGroup1([]));
    dispatch(trendActions.setSegmentGroup2([]));
    dispatch(trendActions.setShowCustomGroupingFilterName(false));
  };
  /**
   * set isDetailed value
   * @param {*} event - onChanged event
   */
  const handleIsDetailedChange = (event) => {
    dispatch(trendActions.setIsSegmentDetailed(event.target.checked));
    dispatch(trendActions.setShowCustomGroupingFilterName(false));
    if (previousFocusOnSelection[chartBreakdown]) {
      if (event.target.checked) {
        previousFocusOnSelection[chartBreakdown].detailedOff = focusOn;
      } else {
        previousFocusOnSelection[chartBreakdown].detailedOn = focusOn;
      }
    }
  };
  // get custom aggregation groups list based on breakdown
  useEffect(() => {
    if (!organizationId || !currentUser || !chartBreakdown) return;
    const query = `limit=${PAGINATION.DROPDOWN_AGGREGATION_FILTER_PER_PAGE}&breakdown=${chartBreakdown}&isDetailed=${isDetailed}&page=0&`;
    dispatch(trendActions.getAggregationFilter({ organizationId, userId: currentUser?.id, query }));
  }, [chartBreakdown, isDetailed]);
  // Trend time command flow implementation
  // focus on list is rebuilt after a breakdown change, until then it holds previous breakdown options
  const currentBreakdown = getSelectedBreakdown(visualFilters, chartBreakdown, isDetailed);
  const isFocusOnListReady = !currentBreakdown || focusOnListBreakdown === currentBreakdown;
  // trend tabular filter command flow implementation
  useTrendTabularCommandPipeline(tabValue, {
    toggleDisabled,
    isLoaded,
    customBooking,
    visualFilters,
    dateComparison,
    movingAverageList,
    chartBreakdownList,
    focusOnList,
    isFocusOnListReady,
    isDetailed,
    hotelList,
    hotelGroupList: [...hotelGroupList, ...predefinedHotelGroups],
    handleAlignmentToggleChange,
    setArbitraryComparisonDate,
    resetOptions,
  });
  //
  return (
    <>
      <PmsToggle
        disabled={toggleDisabled}
        value={pmsSyncToggle}
        onChange={(event) => dispatch(dashboardActions.setPmsSync(event.target.checked))}
      />
      <br />
      <GranularityRadio />
      <br />
      <BookingWindow
        id="auto-trend-booking"
        vOTB={vOTB}
        customBooking={customBooking}
        handleOTBChange={handleOTBChange}
        reportType={reportType}
        latestDate={latestDate}
        otbValue={0}
      />
      <br />
      <WeeklyCumulativeWindow
        isCumulative={isCumulative}
        weeklyViewToggle={weeklyViewToggle}
        handleIsCumulativeChange={(event) =>
          dispatch(trendActions.setIsCumulative(event.target.checked))
        }
        handleWeeklyViewChange={(event) =>
          dispatch(trendActions.setTrendTimeWeeklyView(event.target.checked))
        }
        isForecastToggle
        handleIsForecastChange={(event) =>
          dispatch(trendActions.setIsTrendForecast(event.target.checked))
        }
        isTrendForecast={isTrendForecast}
        isForecastDisabled={isForecastDisabled}
        isPercentageToggle
        isTrendPercentageChange={isTrendPercentageChange}
        isTrendPercentageDisabled={isTrendPercentageDisabled}
        handleIsPercentageToggleChange={(event) =>
          dispatch(trendActions.setIsTrendPercentageChange(event.target.checked))
        }
        isWeeklyViewDisabled={isWeeklyViewDisabled}
        tabName="trend"
      />
      <br />
      <MovingAverageWindow
        movingAverage={movingAverage}
        movingAverageList={movingAverageList ?? []}
        handleMovingAverageChange={(event, value) => dispatch(trendActions.setMovingAverage(value))}
        handleIsMovingAverageChange={(event) => {
          dispatch(trendActions.setIsMovingAverage(event.target.checked));
          if (event.target.checked) {
            dispatch(trendActions.setIsTrendDeAggregate(false));
            if (isTabular && revenueIsDisplay) {
              dispatch(trendActions.setRevenueIsDisplayTT(false));
              setTimeout(() => {
                dispatch(trendActions.setRevenueIsDisplayTT(true));
              }, 100);
            }
          }
        }}
        isMovingAverage={isMovingAverage}
        isDisabled={isCumulative || weeklyViewToggle}
        id="auto-trend-moving-average"
      />
      <br />
      <TrendBreakdownWindow
        id="auto-trend-breakdown"
        isDetailed={isDetailed}
        chartBreakdown={chartBreakdown}
        chartBreakdownList={chartBreakdownList}
        handleIsDetailedChange={handleIsDetailedChange}
        handleBreakdownChange={handleBreakdownChange}
        isFocusOnAvailable
        focusOnList={focusOnList}
        focusOn={focusOn}
        focusOnLabel={focusOn?.length ? 'Focused on' : 'Select Focus on'}
        handleFocusOnChange={(event, value, reason) => {
          if (reason === 'selectOption' || reason === 'removeOption') {
            if (
              value.find((option) => option.label === COMMON_FOCUS_ON_OPTIONS.SELECT_ALL.label) &&
              value?.filter(
                (option) =>
                  option.label !== COMMON_FOCUS_ON_OPTIONS.OVERALL.label &&
                  option.label !== COMMON_FOCUS_ON_OPTIONS.SELECT_ALL.label
              ).length ===
                focusOn.filter((option) => option.label !== COMMON_FOCUS_ON_OPTIONS.OVERALL.label)
                  .length &&
              value?.length === focusOnList.length
            ) {
              // Deselect all options if "Select All" is already selected
              const allOptions = [];
              // Include "Overall" only if it's already selected or if breakdown is active
              if (value.find((option) => option.label === COMMON_FOCUS_ON_OPTIONS.OVERALL.label)) {
                allOptions.push(COMMON_FOCUS_ON_OPTIONS.OVERALL);
              }
              dispatch(trendActions.setTrendSegmentFocusOn(allOptions));
              if (previousFocusOnSelection[chartBreakdown]) {
                if (isDetailed) {
                  previousFocusOnSelection[chartBreakdown].detailedOn = allOptions;
                } else {
                  previousFocusOnSelection[chartBreakdown].detailedOff = allOptions;
                }
              }
              dispatch(trendActions.setIsFocusAllSelected(false));
            } else if (
              value.find((option) => option.label === COMMON_FOCUS_ON_OPTIONS.SELECT_ALL.label)
            ) {
              // Check if all relevant options (excluding "SELECT_ALL" and "OVERALL") have been selected
              const areAllOptionsSelected =
                focusOnList.filter(
                  (option) =>
                    option.label !== COMMON_FOCUS_ON_OPTIONS.SELECT_ALL.label &&
                    option.label !== COMMON_FOCUS_ON_OPTIONS.OVERALL.label
                ).length ===
                value.filter(
                  (option) =>
                    option.label !== COMMON_FOCUS_ON_OPTIONS.SELECT_ALL.label &&
                    option.label !== COMMON_FOCUS_ON_OPTIONS.OVERALL.label
                ).length;

              if (areAllOptionsSelected) {
                // If all options are selected, keep "OVERALL" selected if it exists
                const allOptions = value.find(
                  (option) => option.label === COMMON_FOCUS_ON_OPTIONS.OVERALL.label
                )
                  ? [COMMON_FOCUS_ON_OPTIONS.OVERALL]
                  : [];
                // Update the focus on options and reset "Select All" flag
                dispatch(trendActions.setTrendSegmentFocusOn(allOptions));
                if (previousFocusOnSelection[chartBreakdown]) {
                  if (isDetailed) {
                    previousFocusOnSelection[chartBreakdown].detailedOn = allOptions;
                  } else {
                    previousFocusOnSelection[chartBreakdown].detailedOff = allOptions;
                  }
                }
                dispatch(trendActions.setIsFocusAllSelected(false));
              } else {
                // Otherwise, select all options excluding "SELECT_ALL" and "OVERALL"
                let allOptions = focusOnList.filter(
                  (option) =>
                    option.label !== COMMON_FOCUS_ON_OPTIONS.SELECT_ALL.label &&
                    option.label !== COMMON_FOCUS_ON_OPTIONS.OVERALL.label
                );
                // Include "Overall" only if it's already selected or if breakdown is active
                if (
                  focusOn.some((option) => option.label === COMMON_FOCUS_ON_OPTIONS.OVERALL.label)
                ) {
                  allOptions = [COMMON_FOCUS_ON_OPTIONS.OVERALL, ...allOptions];
                }
                dispatch(trendActions.setTrendSegmentFocusOn(allOptions));
                if (previousFocusOnSelection[chartBreakdown]) {
                  if (isDetailed) {
                    previousFocusOnSelection[chartBreakdown].detailedOn = allOptions;
                  } else {
                    previousFocusOnSelection[chartBreakdown].detailedOff = allOptions;
                  }
                }
                dispatch(trendActions.setIsFocusAllSelected(true));
              }
            } else {
              dispatch(trendActions.setTrendSegmentFocusOn(value));
              if (previousFocusOnSelection[chartBreakdown]) {
                if (isDetailed) {
                  previousFocusOnSelection[chartBreakdown].detailedOn = value;
                } else {
                  previousFocusOnSelection[chartBreakdown].detailedOff = value;
                }
              }
              dispatch(
                trendActions.setIsFocusAllSelected(
                  value?.filter(
                    (option) =>
                      option.label !== COMMON_FOCUS_ON_OPTIONS.OVERALL.label &&
                      option.label !== COMMON_FOCUS_ON_OPTIONS.SELECT_ALL.label
                  ).length ===
                    focusOnList?.filter(
                      (option) =>
                        option.label !== COMMON_FOCUS_ON_OPTIONS.OVERALL.label &&
                        option.label !== COMMON_FOCUS_ON_OPTIONS.SELECT_ALL.label
                    ).length
                )
              );
            }
          }
        }}
        isGroupingAvailable={activeTab === REPORT_TYPE.TREND_SEGMENT && !isTrendDeAggregate}
        onGroupClick={() => {
          setOpen(true);
          dispatch(trendActions.setShowCustomGroupingFilterName(false));
        }}
        setOpen={setOpen}
        openGroupingDialog={open}
        showFilterName={showFilterName}
        setAName={setAName}
        setBName={setBName}
        selectedFilterName={selectedFilterName}
        isDeAggregateToggle
        isTrendDeAggregate={isTrendDeAggregate}
        isTrendDeAggregateDisabled={isTrendDeAggregateDisabled || isMovingAverage}
        handleIsDeAggregateToggleChange={(event) =>
          dispatch(trendActions.setIsTrendDeAggregate(event.target.checked))
        }
        isPrimary={isPrimary}
        handlePrimaryChange={(event) => {
          dispatch(trendActions.setIsPrimary(event.target.checked));
          if (event.target.checked && !isMovingAverage) {
            dispatch(trendActions.setIsTrendDeAggregate(true));
            dispatch(trendActions.setIsTrendDeAggregateDisabled(false));
          }
        }}
        allSelected={allSelected}
      />

      <br />
      <CompareHotelsWindow
        selectedHotelName={selectHotelName}
        hotelList={hotelList ?? []}
        mergedHotelList={mergedHotelList ?? []}
        focusOn={hotelFocusOn}
        focusOnLabel={hotelFocusOn?.length ? 'Compared with' : 'Select Hotel'}
        handleCompareHotelsChange={(event, value) => {
          dispatch(trendActions.setTrendHotelFocusOn(value));
        }}
        normalizedViewToggle={normalizedViewToggle}
        handleTrendHotelNormalize={(event) =>
          dispatch(trendActions.setTrendHotelNormalizedView(event.target.checked))
        }
        enableSecondaryHotelGroups={enableSecondaryHotelGroups}
        hotelGroupList={hotelGroupList}
        isTrendPercentageChange={isTrendPercentageChange}
        originalHotelFocusOn={originalHotelFocusOn}
        isDemoModeActivated={isDemoModeActivated}
        setOriginalHotelFocusOn={(focus) => {
          dispatch(trendActions.setOriginalHotelFocusOn(focus));
        }}
        resetFocusOn={(focus) => {
          dispatch(trendActions.resetHotelFocusOn(focus));
        }}
      />
      <br />
      <TrendTimeVsComparisonWindow
        customBooking={customBooking}
        alignmentToggle={alignmentToggle}
        handleToggleChange={handleAlignmentToggleChange}
        symmetricComparisonToggle={symmetricComparisonToggle}
        visual={visual}
        visualName={visualName}
        dateComparison={dateComparison}
        dateComparisonList={dateComparisonList}
        handleComparisonChange={handleDateComparisonChange}
        handleSymmetricComparisonChange={(event) =>
          dispatch(trendActions.setTrendSymmetricComparisonToggle(event.target.checked))
        }
        weeklyViewToggle={weeklyViewToggle}
        customDate={customDate}
        reportType={reportType}
        startDate={startDate}
        endDate={endDate}
        targetDate={targetDate}
        handleCustomDates={(event) =>
          dispatch(trendActions.setTrendTimeCustomDate(event.target.value))
        }
        isDisplay={symmetricComparisonIsDisplay}
        isDisplayOnClickAction={() =>
          dispatch(trendActions.setSymmetricComparisonIsDisplay(!symmetricComparisonIsDisplay))
        }
        showBreakdown={false}
        activeTab={activeTab}
        isPrimary={isPrimary}
        handlePrimaryChange={(event) => dispatch(trendActions.setIsPrimary(event.target.checked))}
        id="auto-trend-time-comparison-window"
      />
      <br />
      <CustomStack cssClassName={styles.innerFilter}>
        <ActionButton onClick={clearSearch} label="RESET" />
      </CustomStack>
      <Toolbar />
      <DataUpdatedLabel
        latestDate={latestDate}
        hotelTimezone={hotelTimezone}
        toggleDisabled={toggleDisabled}
      />
    </>
  );
};
//
export default TrendFilter;
